import AssetsLoader from '../utils/assetsLoader';
import { initGlobalEventEmitter, getGlobalEventEmitter } from '../utils/globalEventEmitter';

import SceneManager from './sceneManager';

function findCardInstance(gameState, cardInstanceId) {
    // Check player's field characters
    const playerCharacter = gameState.player.field_characters.find(character => character.instanceId === cardInstanceId);
    if (playerCharacter) {
        return playerCharacter;
    }

    // Check player's hand
    const playerHandCard = gameState.player.hand.find(card => card.instanceId === cardInstanceId);
    if (playerHandCard) {
        return playerHandCard;
    }

    // Check opponent's field characters
    const opponentCharacter = gameState.opponent.field_characters.find(character => character.instanceId === cardInstanceId);
    if (opponentCharacter) {
        return opponentCharacter;
    }

    // Check player's leader
    if (gameState.player.leader.instanceId === cardInstanceId) {
        return gameState.player.leader;
    }

    // Check opponent's leader
    if (gameState.opponent.leader.instanceId === cardInstanceId) {
        return gameState.opponent.leader;
    }

    // check players trash
    const playerTrashCard = gameState.player.trash.find(card => card.instanceId === cardInstanceId);
    if (playerTrashCard) {
        return playerTrashCard;
    }

    // check opponent's trash
    const opponentTrashCard = gameState.opponent.trash.find(card => card.instanceId === cardInstanceId);
    if (opponentTrashCard) {
        return opponentTrashCard;
    }

    // Not found
    return null;
}

export default class GameManager {
    constructor(findMatchMode) {
        this.app = null;
        this.sceneManager = new SceneManager();
        this.socket = null;
        this.serverGameState = {};
        this.clientGameState = {
            donsSelectedForReturn: [],
        };
        this.findMatchMode = findMatchMode;
    }

    async init(socket, storage) {
        const eventEmitter = initGlobalEventEmitter();
        this.setupEventListeners(eventEmitter);
        this.socket = socket;
        this.storage = storage;
        
        await AssetsLoader.init();
    }

    cleanup() {
        const eventEmitter = getGlobalEventEmitter();
        this.removeEventListeners(eventEmitter);
        this.stopBackgroundMusic();
        this.sceneManager.cleanup();
    }

    attachToPixiApp(app) {
        app.stage.addChild(this.sceneManager.scene);
    }

    resize(width, height) {
        this.sceneManager.resize(width, height);
    }

    detachFromPixiApp(app) {
        app.stage.removeChild(this.sceneManager.scene);
    }

    setupEventListeners(eventEmitter) {
        eventEmitter.on('cardHeld', this.#handleCardHeld, this);
        eventEmitter.on('cardTapped', this.#handleCardTapped, this);
        eventEmitter.on('stageClicked', this.#handleStageClicked, this);
        eventEmitter.on('playCardClicked', this.#handlePlayCardClicked, this);
        eventEmitter.on('endTurnClicked', this.#handleEndTurnButtonClicked, this);
        eventEmitter.on('declareAttackClicked', this.#handleDeclareAttackClicked, this);
        eventEmitter.on('cancelClicked', this.#handleCancelClicked, this);
        eventEmitter.on('giveDonClicked', this.#handleGiveDonClicked, this);
        eventEmitter.on('activateEffectClicked', this.#handleActivateEffectClicked, this);
        eventEmitter.on('passClicked', this.#handlePassClicked, this);
        eventEmitter.on('blockClicked', this.#handleBlockClicked, this);
        eventEmitter.on('counterClicked', this.#handleCounterClicked, this);
        eventEmitter.on('activeDonsClicked', this.#handleActiveDonsClicked, this);
        eventEmitter.on('restedDonsClicked', this.#handleRestedDonsClicked, this);
        eventEmitter.on('confirmClicked', this.#handleConfirmClicked, this);
        eventEmitter.on('resetClicked', this.#handleResetClicked, this);
        eventEmitter.on('keepHandClicked', this.#handleKeepHandClicked, this);
        eventEmitter.on('replaceHandClicked', this.#handleReplaceHandClicked, this);
        eventEmitter.on('trashClicked', this.#handleTrashClicked, this);
        eventEmitter.on('keepTriggerClicked', this.#handleKeepTriggerClicked, this);
        eventEmitter.on('activateTriggerClicked', this.#handleActivateTriggerClicked, this);
        eventEmitter.on('closeCardInfo', this.#handleCloseCardInfo, this);
        eventEmitter.on('settingsButtonClicked', this.#handleSettingsButtonClicked, this);
        eventEmitter.on('surrenderButtonClicked', this.#handleSurrenderButtonClicked, this);
        eventEmitter.on('closeFtuePopupClicked', this.#handleCloseFtuePopupClicked, this);
        eventEmitter.on('rematchButtonClicked', this.#handleRematchButtonClicked, this);
        eventEmitter.on('quitMatchButtonClicked', this.#handleQuitMatchButtonClicked, this);
        eventEmitter.on('searchDeckReorderSlotsUpdated', this.#handleSearchDeckReorderSlotsUpdated, this);
        eventEmitter.on('noSelectionClicked', this.#handleNoSelectionClicked, this);
        eventEmitter.on('placeOnTopClicked', this.#handlePlaceOnTopClicked, this);
        eventEmitter.on('placeOnBottomClicked', this.#handlePlaceOnBottomClicked, this);
    }

    removeEventListeners(eventEmitter) {
        eventEmitter.removeAllListeners();
    }

    #handleEndTurnButtonClicked() {
        this.clientGameState.availableActions = [];
        this.socket.emit('playerAction', { type: 'PASS_TURN', payload: {} });
    }

    async updateGameState(newGameState) {
        this.clientGameState.isFindMatchMode = this.findMatchMode;
        
        const isMyTurn = newGameState.isMyTurn;
        const isInEffectStepResolution = newGameState.pendingAction?.type === 'EFFECT_STEP_RESOLUTION';
        const expectedResponseType = newGameState.pendingAction?.expectedResponseType;
        const activeDonsCount = newGameState.player.active_don.filter(don => !don.isRested).length;
        const isInTrashCharacterState = newGameState.pendingAction?.type === 'TRASH_CHARACTER_PROMPT';
        const isMyTrashCharacterState = isInTrashCharacterState && newGameState.pendingAction?.playerId === newGameState.player.playerId;
        const isEnemyCounterState = isMyTurn && newGameState.pendingAction?.type === 'COUNTER_STEP';
        const isEnemyBlockerState = isMyTurn && newGameState.pendingAction?.type === 'BLOCKER_STEP';
        const isMulliganStep = newGameState.pendingAction?.type === 'MULLIGAN_PROMPT';

        let isMyEffectStep = false;
        if (isInEffectStepResolution) {
            if (expectedResponseType === 'targetSelection') {
                if (newGameState.pendingAction?.targetingPlayerId === newGameState.player.playerId) {
                    isMyEffectStep = true;
                }
            } else {
                if (newGameState.pendingAction?.context?.triggeringPlayerId === newGameState.player.playerId) {
                    isMyEffectStep = true;
                }
            }
        }

        if (this.clientGameState?.selectedCard) {
            const selectedCardInstance = findCardInstance(newGameState, this.clientGameState.selectedCard);
            if (selectedCardInstance) {
                this.clientGameState.selectedCardInstance = selectedCardInstance;
            }
        }

        this.clientGameState.selectableCards = [];
        this.clientGameState.playableCards = [];

        const animations = this.#generateAnimations(newGameState);
        if (animations.length) {
            if (this.sceneManager.firstUpdate) this.refreshScene();
            this.sceneManager.disableClicks();
            await this.sceneManager.playAnimations(animations);
            this.sceneManager.enableClicks();
        }

        this.clientGameState.enemyCounterMode = false;
        this.clientGameState.enemyBlockerMode = false;
        this.clientGameState.noMoreMoves = false;
        this.clientGameState.opponentSearchDeckMode = false;
        this.clientGameState.selectingFromTrashMode = false;

        if (isMyTurn) {
            if (isInEffectStepResolution) {
                if (isMyEffectStep) {
                    if (expectedResponseType === 'targetSelection') {
                        const validTargets = newGameState.pendingAction.validTargetsForCurrentStep;
                        const searchScope = newGameState.pendingAction.currentStepData.targetCriteria.scope;
                        const searchZone = newGameState.pendingAction.currentStepData.targetCriteria.zone;
                        this.clientGameState.choosingEffectTargetMode = true;
                        this.clientGameState.selectableCards = validTargets;

                        if (validTargets.length === 0) {
                            this.clientGameState.overridePrompt = 'No Cards to Select';
                        }

                        if (searchZone === 'Trash') {
                            this.clientGameState.selectingFromTrashMode = true;
                        }

                        if (searchScope === 'Opponent' && searchZone === 'Hand') {
                            this.clientGameState.choosingFromOpponentHandMode = true;
                        }
                    } else if (expectedResponseType === 'returnDons') {
                        this.clientGameState.choosingReturnDonsMode = true;
                        this.clientGameState.selectableCards = newGameState.pendingAction.validTargetsForCurrentStep;
                        this.clientGameState.overridePrompt = `Return ${newGameState.pendingAction.requiredSelectionCount} DON!!s - (0/${newGameState.pendingAction.requiredSelectionCount})`;
                        this.clientGameState.donsSelectedForReturn = [];
                        this.clientGameState.availableActions = ['RESET', 'CONFIRM'];
                    } else if (expectedResponseType === 'searchDeck') {
                        this.clientGameState.searchDeckMode = true;

                        if (newGameState.pendingAction.searchCards.length === 1) {
                            this.clientGameState.searchDeckSingleCardMode = true;
                            if (newGameState.pendingAction.validTargetsForCurrentStep.length === 1) {
                                this.clientGameState.selectedCard = newGameState.pendingAction.validTargetsForCurrentStep[0];
                            }
                        }

                        if (newGameState.pendingAction.currentStepData.details.forceNoSelection) {
                            this.#setSearchDeckReorderMode(newGameState);
                        }
                    } else if (expectedResponseType === 'prompt') {
                        this.clientGameState.promptMode = true;
                        this.clientGameState.availableActions = ['CONFIRM'];
                    }
                } else {
                    if (expectedResponseType === 'searchDeck') {
                        this.clientGameState.opponentSearchDeckMode = true;
                    }
                }
            } else if (isMyTrashCharacterState) {
                this.clientGameState.choosingTrashCharacterMode = true;
                this.clientGameState.selectableCards = newGameState.pendingAction.fieldCharacterIds;
            } else if (isEnemyCounterState) {
                this.clientGameState.enemyCounterMode = true;
            } else if (isEnemyBlockerState) {
                this.clientGameState.enemyBlockerMode = true;
            } else {
                this.clientGameState.playableCards = newGameState.player.hand.filter(card => card.currentCost <= activeDonsCount && (card.type !== 'Event' || card.isEventMain)).map(card => card.instanceId);

                if ((!newGameState.player.leader.isRested && newGameState.turnNumber > 2) || newGameState.player.leader.canActivateEffect) {
                    if (!newGameState.player.leader.hasCannotAttack) {
                        this.clientGameState.selectableCards.push(newGameState.player.leader.instanceId);
                    }
                }

                newGameState.player.field_characters.forEach(character => {
                    if ((!character.isRested && newGameState.turnNumber > 2 && (character.turnPlayed !== newGameState.turnNumber || character.hasRush)) || character.canActivateEffect) {
                        if (!character.hasCannotAttack) {
                            this.clientGameState.selectableCards.push(character.instanceId);
                        }
                    }
                });

                newGameState.player.field_stages.forEach(stage => {
                    if (stage.canActivateEffect) {
                        this.clientGameState.selectableCards.push(stage.instanceId);
                    }
                });

                if (newGameState.player.active_don.filter(don => !don.isRested).length) {
                    this.clientGameState.selectableCards.push(newGameState.player.leader.instanceId, ...newGameState.player.field_characters.map(character => character.instanceId));
                }

                if (this.clientGameState.selectedCard) {
                    let selectedCardInstance = newGameState.player.field_characters.find(character => character.instanceId === this.clientGameState.selectedCard);
                    if (!selectedCardInstance && newGameState.player.leader.instanceId === this.clientGameState.selectedCard) {
                        selectedCardInstance = newGameState.player.leader;
                    }

                    if (selectedCardInstance && selectedCardInstance.canActivateEffect) {
                        if (!this.clientGameState.availableActions?.includes('ACTIVATE_EFFECT')) {
                            this.clientGameState.availableActions?.push('ACTIVATE_EFFECT');
                        }
                    } else {
                        if (this.clientGameState.availableActions?.includes('ACTIVATE_EFFECT')) {
                            this.clientGameState.availableActions = this.clientGameState.availableActions?.filter(action => action !== 'ACTIVATE_EFFECT');
                        }
                    }
                }

                if (newGameState.currentPhase === 'Main' && this.clientGameState.selectableCards.length === 0 && this.clientGameState.playableCards.length === 0) {
                    this.clientGameState.noMoreMoves = true;
                }
            }


        } else {
            this.clientGameState.choosingBlockerMode = false;
            this.clientGameState.choosingCounterMode = false;

            if (isInEffectStepResolution) {
                if (isMyEffectStep) {
                    if (expectedResponseType === 'targetSelection') {
                        const validTargets = newGameState.pendingAction.validTargetsForCurrentStep;
                        const searchZone = newGameState.pendingAction.currentStepData.targetCriteria.zone;
                        this.clientGameState.choosingEffectTargetMode = true;
                        this.clientGameState.selectableCards = validTargets;

                        if (validTargets.length === 0) {
                            this.clientGameState.overridePrompt = 'No Cards to Select';
                        }

                        if (searchZone === 'Trash') {
                            this.clientGameState.selectingFromTrashMode = true;
                        }
                    } else if (expectedResponseType === 'returnDons') {
                        this.clientGameState.choosingReturnDonsMode = true;
                        this.clientGameState.selectableCards = newGameState.pendingAction.validTargetsForCurrentStep;
                        this.clientGameState.overridePrompt = `Return ${newGameState.pendingAction.requiredSelectionCount} DON!!s - (0/${newGameState.pendingAction.requiredSelectionCount})`;
                        this.clientGameState.donsSelectedForReturn = [];
                        this.clientGameState.availableActions = ['RESET', 'CONFIRM'];
                    } else if (expectedResponseType === 'searchDeck') {
                        this.clientGameState.searchDeckMode = true;

                        if (newGameState.pendingAction.currentStepData.details.forceNoSelection) {
                            this.#setSearchDeckReorderMode(newGameState);
                        }
                    } else if (expectedResponseType === 'prompt') {
                        this.clientGameState.promptMode = true;
                        this.clientGameState.availableActions = ['CONFIRM'];
                    }
                } else {
                    if (expectedResponseType === 'searchDeck') {
                        this.clientGameState.opponentSearchDeckMode = true;
                    }
                }
            } else if (newGameState.pendingAction?.type === 'BLOCKER_STEP') {
                this.clientGameState.choosingBlockerMode = true;
                this.clientGameState.validBlockers = newGameState.pendingAction.potentialBlockerIds;
                this.clientGameState.availableActions = [];
            } else if (newGameState.pendingAction?.type === 'COUNTER_STEP') {
                this.clientGameState.choosingCounterMode = true;
                this.clientGameState.playableCards = newGameState.player.hand.filter(card => card.isCounter).map(card => card.instanceId);
                this.clientGameState.availableActions = [];
            } else if (isMyTrashCharacterState) {
                this.clientGameState.choosingTrashCharacterMode = true;
                this.clientGameState.selectableCards = newGameState.pendingAction.fieldCharacterIds;
            }
        }

        if (newGameState.currentPhase === 'GameOver') {
            this.stopBackgroundMusic();
             if (newGameState.winner === newGameState.player.playerId) {
                this.clientGameState.endResult = 'victory';
             } else if (newGameState.winner === newGameState.opponent.playerId) {
                this.clientGameState.endResult = 'defeat';
             }
        }

        this.serverGameState = newGameState;
        this.serverGameState.clientGameState = this.clientGameState;
        this.sceneManager.updateScene(newGameState);
    }

    refreshScene() {
        this.sceneManager.updateScene(this.serverGameState);
    }

    showMatchRewards(reward) {
        this.sceneManager.showMatchRewards(reward);
    }

    async startGame() {
        await this.sceneManager.fadeIn();

        const ftuePopupShown = this.storage?.getItem('ftuePopupShown');
        if (+ftuePopupShown < 3) {
            this.sceneManager.showFtuePopup();
        }
    }

    #handleCardHeld(cardContainer) {
        if (!cardContainer.cardInstance) return;

        this.clientGameState.cardHeld = true;
        this.clientGameState.heldCardInstance = cardContainer.cardInstance;
        this.sceneManager.showCardInfo(cardContainer.cardInstance);
    }

    #handleCardTapped(cardContainer) {
        if (this.serverGameState.pendingAction?.type === 'MULLIGAN_PROMPT') {
            return;
        }

        if (this.clientGameState.choosingAttackTargetMode) {
            if (!this.clientGameState.validAttackTargets.includes(cardContainer.cardInstance.instanceId)) {
                return;
            }

            this.clientGameState.choosingAttackTargetMode = false;
            this.clientGameState.overridePrompt = '';
            this.clientGameState.validAttackTargets = [];
            this.socket.emit('playerAction', { type: 'DECLARE_ATTACK', payload: { attackerId: this.clientGameState.selectedCard, defenderId: cardContainer.cardInstance.instanceId } });
            this.clientGameState.selectedCard = null;
            this.refreshScene();
            return;
        }

        if (this.clientGameState.choosingBlockerMode) {
            if (this.clientGameState.validBlockers.includes(cardContainer.cardInstance.instanceId)) {
                this.clientGameState.availableActions = ['BLOCK'];
                this.clientGameState.selectedCard = cardContainer.cardInstance.instanceId;
            } else {
                this.clientGameState.availableActions = [];
                this.clientGameState.selectedCard = null;
            }

            this.refreshScene();
            return;
        }

        if (this.clientGameState.choosingCounterMode) {
            if (this.clientGameState.playableCards.includes(cardContainer.cardInstance.instanceId)) {
                this.clientGameState.availableActions = ['COUNTER'];
                this.clientGameState.selectedCard = cardContainer.cardInstance.instanceId;
                this.clientGameState.selectedCardType = cardContainer.cardInstance.type;
            } else {
                this.clientGameState.availableActions = [];
                this.clientGameState.selectedCard = null;
                this.clientGameState.selectedCardType = null;
            }

            this.refreshScene();
            return;
        }

        if (this.clientGameState.choosingReturnDonsMode) {
            if (this.clientGameState.donsSelectedForReturn.length >= this.serverGameState.pendingAction.requiredSelectionCount) {
                return;
            }

            if (cardContainer.cardInstance.attachedDon.some(don => this.clientGameState.selectableCards.includes(don.instanceId))) {
                const selectableAttachedDons = cardContainer.cardInstance.attachedDon.filter(don => !this.clientGameState.donsSelectedForReturn.includes(don.instanceId));
                if (selectableAttachedDons.length) {
                    this.#selectDonForReturn(selectableAttachedDons[0].instanceId);
                }
            }

            console.log('donsSelectedForReturn ', this.clientGameState.donsSelectedForReturn.length, ' of ', this.serverGameState.pendingAction.requiredSelectionCount);
            this.refreshScene();
            return;
        }

        if (this.clientGameState.searchDeckMode) {
            const clickedCardInstanceId = cardContainer.cardInstance.instanceId;
            if (this.serverGameState.pendingAction.validTargetsForCurrentStep.includes(clickedCardInstanceId)) {
                this.clientGameState.selectedCard = clickedCardInstanceId;
            }

            this.refreshScene();
            return;
        }

        if (this.clientGameState.selectingFromTrashMode) {
            if (this.serverGameState.pendingAction.validTargetsForCurrentStep.includes(cardContainer.cardInstance.instanceId)) {
                this.clientGameState.selectedCard = cardContainer.cardInstance.instanceId;
            }
            this.refreshScene();
            return;
        }

        if (this.clientGameState.choosingFromOpponentHandMode) {
            const clickedCardInstanceId = cardContainer.cardInstance.instanceId;
            if (this.serverGameState.pendingAction.validTargetsForCurrentStep.includes(clickedCardInstanceId)) {
                this.clientGameState.selectedCard = clickedCardInstanceId;
            }

            this.refreshScene();
            return;
        }

        if (this.serverGameState.pendingAction?.type === 'EFFECT_STEP_RESOLUTION') {
            if (this.clientGameState.selectableCards.includes(cardContainer.cardInstance.instanceId)) {
                this.socket.emit('playerAction', { type: 'RESOLVE_EFFECT_STEP_V2', payload: { chosenTargetIds: [cardContainer.cardInstance.instanceId] } });
                this.clientGameState.choosingEffectTargetMode = false;
                this.clientGameState.selectableCards = [];
            }
            
            this.refreshScene();
            return;
        }

        if (this.clientGameState.choosingTrashCharacterMode) {
            if (this.clientGameState.selectableCards.includes(cardContainer.cardInstance.instanceId)) {
                this.socket.emit('playerAction', { type: 'RESOLVE_TRASH_CHARACTER_PROMPT', payload: { characterToTrashId: cardContainer.cardInstance.instanceId } });
                this.clientGameState.choosingTrashCharacterMode = false;
                this.clientGameState.selectableCards = [];
            }
            this.refreshScene();
            return;
        }

        if (this.clientGameState.enemyCounterMode) {
            return;
        }

        const isMyTurn = this.serverGameState.isMyTurn;
        const activeDonsCount = this.serverGameState.player.active_don.filter(don => !don.isRested).length;

        const isMyLeader = cardContainer.cardInstance.instanceId === this.serverGameState.player.leader.instanceId;
        const isMyCharacter = this.serverGameState.player.field_characters.map(character => character.instanceId).includes(cardContainer.cardInstance.instanceId);
        const isMyStage = cardContainer.cardInstance.instanceId === this.serverGameState.player.field_stages[0]?.instanceId;

        this.clientGameState.selectedCard = cardContainer.cardInstance.instanceId;
        this.clientGameState.selectedCardInstance = cardContainer.cardInstance;

        this.clientGameState.availableActions = [];

        if (cardContainer.cardInstance.location === 'Hand' && isMyTurn && cardContainer.cardInstance.currentCost <= activeDonsCount && (cardContainer.cardInstance.type !== 'Event' || cardContainer.cardInstance.isEventMain)) {
            this.clientGameState.availableActions = ['PLAY_CARD'];
        } else if ((isMyLeader || isMyCharacter || isMyStage) && isMyTurn) {
            if (this.serverGameState.player.active_don.filter(don => !don.isRested).length && cardContainer.cardInstance.location !== 'field_stages') {
                this.clientGameState.availableActions.push('GIVE_DON');
            }

            if (!cardContainer.cardInstance.isRested &&
                this.serverGameState.turnNumber > 2 &&
                (cardContainer.cardInstance.turnPlayed !== this.serverGameState.turnNumber || cardContainer.cardInstance.hasRush) &&
                cardContainer.cardInstance.location !== 'field_stages' &&
                !cardContainer.cardInstance.hasCannotAttack) {
                this.clientGameState.availableActions.push('DECLARE_ATTACK');
            }

            if (cardContainer.cardInstance.canActivateEffect) {
                this.clientGameState.availableActions.push('ACTIVATE_EFFECT');
            }
        }

        this.sceneManager.hidePlayerTrashOverlay();
        this.sceneManager.hideOpponentTrashOverlay();
        this.refreshScene();
        this.onCardTapped?.(cardContainer);
    }

    #handleStageClicked() {
        if (this.clientGameState.searchDeckMode) {
            if (!this.clientGameState.searchDeckReorderMode) {
                if (this.clientGameState.searchDeckSingleCardMode) {
                    return;
                } else {
                    this.clientGameState.selectedCard = null;
                    this.refreshScene();
                }
            }
            
            return;
        }

        if (this.clientGameState.selectingFromTrashMode) {
            return;
        }

        if (this.clientGameState.promptMode) {
            return;
        }

        if (!this.clientGameState.choosingReturnDonsMode) {
            this.clientGameState.selectedCard = null;
            this.clientGameState.availableActions = [];
        }

        this.sceneManager.hidePlayerTrashOverlay();
        this.sceneManager.hideOpponentTrashOverlay();
        this.refreshScene();
    }

    #handlePlayCardClicked() {
        this.socket.emit('playerAction', { type: 'PLAY_CARD', payload: { cardInstanceId: this.clientGameState.selectedCard } });
        this.clientGameState.selectedCard = null;
        this.clientGameState.availableActions = [];
    }

    #handleDeclareAttackClicked() {
        this.clientGameState.choosingAttackTargetMode = true;
        this.clientGameState.overridePrompt = 'Select a Target';
        this.clientGameState.availableActions = [];
        this.clientGameState.validAttackTargets = [];

        const possibleTargets = [this.serverGameState.opponent.leader, ...this.serverGameState.opponent.field_characters];
        possibleTargets.forEach(target => {
            let canAttackTarget = true, canTargetBeAttacked = true;
            
            if (target.type === 'Character' && !target.isRested && !this.clientGameState.selectedCardInstance.canAttackActiveCharacters) {
                canAttackTarget = false;
            }

            if (target.hasCannotBeAttacked) {
                canTargetBeAttacked = false;
            }

            if (canAttackTarget && canTargetBeAttacked) {
                this.clientGameState.validAttackTargets.push(target.instanceId);
            }
        });

        this.refreshScene();
    }

    #handleCancelClicked() {
        this.clientGameState.choosingAttackTargetMode = false;
        this.clientGameState.overridePrompt = '';
        this.clientGameState.validAttackTargets = [];
        this.refreshScene();
    }

    #handleGiveDonClicked() {
        const playerActiveDons = this.serverGameState.player.active_don.filter(don => !don.isRested);
        if (!playerActiveDons.length) {
            return;
        }

        const donInstanceId = playerActiveDons[0]?.instanceId;
        this.socket.emit('playerAction', { type: 'ATTACH_DON', payload: { donInstanceId, targetCardInstanceId: this.clientGameState.selectedCard } });

        if (playerActiveDons.length === 1) {
            // remove GIVE_DON from available actions
            this.clientGameState.availableActions = this.clientGameState.availableActions.filter(action => action !== 'GIVE_DON');
        }
    }

    #handleActivateEffectClicked() {
        this.socket.emit('playerAction', { type: 'ACTIVATE_EFFECT', payload: { sourceCardId: this.clientGameState.selectedCard } });
        this.clientGameState.selectedCard = null;
        this.clientGameState.availableActions = [];
    }

    #handlePassClicked() {
        if (this.clientGameState.choosingBlockerMode) {
            this.clientGameState.choosingBlockerMode = false;
            this.clientGameState.selectedCard = null;
            this.clientGameState.availableActions = [];
            this.clientGameState.validBlockers = [];
            this.socket.emit('playerAction', { type: 'RESOLVE_BLOCKER_STEP', payload: { } });
            return;
        }

        if (this.clientGameState.choosingCounterMode) {
            this.clientGameState.choosingCounterMode = false;
            this.clientGameState.selectedCard = null;
            this.clientGameState.availableActions = [];
            this.socket.emit('playerAction', { type: 'PASS_COUNTER_STEP', payload: { } });
            return;
        }

        if (this.clientGameState.choosingEffectTargetMode) {
            this.clientGameState.choosingEffectTargetMode = false;
            this.clientGameState.selectedCard = null;
            this.clientGameState.selectableCards = [];
            this.socket.emit('playerAction', { type: 'RESOLVE_EFFECT_STEP_V2', payload: { } });
            return;
        }

        if (this.clientGameState.choosingReturnDonsMode) {
            this.clientGameState.choosingReturnDonsMode = false;
            this.clientGameState.selectedCard = null;
            this.clientGameState.selectableCards = [];
            this.clientGameState.donsSelectedForReturn = [];
            this.clientGameState.availableActions = [];
            this.clientGameState.overridePrompt = '';
            this.socket.emit('playerAction', { type: 'RESOLVE_EFFECT_STEP_V2', payload: { skipped: true } });
            return;
        }

        if (this.clientGameState.promptMode) {
            this.clientGameState.promptMode = false;
            this.clientGameState.selectedCard = null;
            this.clientGameState.selectableCards = [];
            this.clientGameState.availableActions = [];
            this.socket.emit('playerAction', { type: 'RESOLVE_EFFECT_STEP_V2', payload: { response: 'pass' } });
            return;
        }
    }

    #handleBlockClicked() {
        this.socket.emit('playerAction', { type: 'RESOLVE_BLOCKER_STEP', payload: { chosenBlockerId: this.clientGameState.selectedCard } });
        this.clientGameState.choosingBlockerMode = false;
        this.clientGameState.selectedCard = null;
        this.clientGameState.availableActions = [];
        this.clientGameState.validBlockers = [];
    }

    #handleCounterClicked() {
        if (this.clientGameState.selectedCardType === 'Event') {
            this.socket.emit('playerAction', { type: 'PLAY_COUNTER_EVENT', payload: { cardInstanceId: this.clientGameState.selectedCard } });
        } else {
            this.socket.emit('playerAction', { type: 'RESOLVE_COUNTER_STEP', payload: { discardedCardIds: [this.clientGameState.selectedCard] } });
        }

        this.clientGameState.selectedCard = null;
        this.clientGameState.selectedCardType = null;
        this.clientGameState.availableActions = [];
    }

    #handleActiveDonsClicked(playerId) {
        if (this.clientGameState.choosingReturnDonsMode) {
            if (this.clientGameState.donsSelectedForReturn.length >= this.serverGameState.pendingAction.requiredSelectionCount) {
                return;
            }

            const player = this.serverGameState.player.playerId === playerId ? this.serverGameState.player : this.serverGameState.opponent;
            const selectableAttachedDons = player.active_don.filter(don => !don.isRested && !this.clientGameState.donsSelectedForReturn.includes(don.instanceId));
            if (selectableAttachedDons.length) {
                this.#selectDonForReturn(selectableAttachedDons[0].instanceId);
            }
        }

        this.refreshScene();
    }

    #handleRestedDonsClicked(playerId) {
        if (this.clientGameState.choosingEffectTargetMode) {
            const player = this.serverGameState.player.playerId === playerId ? this.serverGameState.player : this.serverGameState.opponent;
            const cardInstanceId = player.active_don.filter(don => don.isRested).map(don => don.instanceId)[0];
            if (cardInstanceId) {
                this.socket.emit('playerAction', { type: 'RESOLVE_EFFECT_STEP_V2', payload: { chosenTargetIds: [cardInstanceId] } });
                this.clientGameState.choosingEffectTargetMode = false;
            }
        }

        if (this.clientGameState.choosingReturnDonsMode) {
            if (this.clientGameState.donsSelectedForReturn.length >= this.serverGameState.pendingAction.requiredSelectionCount) {
                return;
            }

            const player = this.serverGameState.player.playerId === playerId ? this.serverGameState.player : this.serverGameState.opponent;
            const selectableAttachedDons = player.active_don.filter(don => don.isRested && !this.clientGameState.donsSelectedForReturn.includes(don.instanceId));
            if (selectableAttachedDons.length) {
                this.#selectDonForReturn(selectableAttachedDons[0].instanceId);
            }
        }

        this.refreshScene();
    }

    #selectDonForReturn(instanceId) {
        this.clientGameState.donsSelectedForReturn.push(instanceId);
        this.clientGameState.overridePrompt = `Return ${this.serverGameState.pendingAction.requiredSelectionCount} DON!!s - (${this.clientGameState.donsSelectedForReturn.length}/${this.serverGameState.pendingAction.requiredSelectionCount})`;
        this.clientGameState.availableActions = ['RESET', 'CONFIRM'];
    }

    #setSearchDeckReorderMode(gameState) {
        this.clientGameState.searchDeckReorderMode = true;
        this.clientGameState.searchDeckReorderSlots = gameState.pendingAction.searchCards.filter(cardInstance => cardInstance.instanceId !== this.clientGameState.selectedCard);
        this.clientGameState.searchDeckChooseReorderPosition = false;
        if (gameState.pendingAction.currentStepData?.details?.placeRest === 'Choose') {
            this.clientGameState.searchDeckChooseReorderPosition = true;
        }
    }

    #handleConfirmClicked() {
        if (this.clientGameState.searchDeckMode) {
            if (!this.clientGameState.searchDeckReorderMode) {
                if (this.serverGameState.pendingAction.currentStepData?.details?.reorder) {
                    this.#setSearchDeckReorderMode(this.serverGameState);
                    this.refreshScene();
                } else {
                    const chosenTargetIds = this.clientGameState.selectedCard ? [this.clientGameState.selectedCard] : [];
                    this.socket.emit('playerAction', { type: 'RESOLVE_EFFECT_STEP_V2', payload: { chosenTargetIds } });
                    this.clientGameState.searchDeckMode = false;
                    this.clientGameState.selectedCard = null;
                    this.refreshScene();
                }
                return;
            } else {
                const order = this.clientGameState.searchDeckReorderSlots.map(cardInstance => cardInstance.instanceId);
                const chosenTargetIds = this.clientGameState.selectedCard ? [this.clientGameState.selectedCard] : [];
                this.socket.emit('playerAction', { type: 'RESOLVE_EFFECT_STEP_V2', payload: { chosenTargetIds, order } });
                this.clientGameState.searchDeckMode = false;
                this.clientGameState.searchDeckReorderMode = false;
                this.clientGameState.selectedCard = null;
                this.clientGameState.searchDeckReorderSlots = [];
                this.refreshScene();
            }
        } else if (this.clientGameState.choosingFromOpponentHandMode) {
            this.socket.emit('playerAction', { type: 'RESOLVE_EFFECT_STEP_V2', payload: { chosenTargetIds: [this.clientGameState.selectedCard] } });
            this.clientGameState.choosingEffectTargetMode = false;
            this.clientGameState.choosingFromOpponentHandMode = false;
            this.clientGameState.selectedCard = null;
            this.clientGameState.selectableCards = [];
            this.clientGameState.playableCards = [];
            this.refreshScene();
        } else if (this.clientGameState.choosingReturnDonsMode) {
            if (this.clientGameState.donsSelectedForReturn.length !== this.serverGameState.pendingAction.requiredSelectionCount) {
                return;
            }
    
            this.socket.emit('playerAction', { type: 'RESOLVE_EFFECT_STEP_V2', payload: { selectedDonIds: this.clientGameState.donsSelectedForReturn } });
            this.clientGameState.choosingReturnDonsMode = false;
            this.clientGameState.selectedCard = null;
            this.clientGameState.selectableCards = [];
            this.clientGameState.donsSelectedForReturn = [];
            this.clientGameState.overridePrompt = '';
            this.clientGameState.availableActions = [];
            this.refreshScene();
        } else if (this.clientGameState.selectingFromTrashMode) {
            const chosenTargetIds = this.clientGameState.selectedCard ? [this.clientGameState.selectedCard] : [];
            this.socket.emit('playerAction', { type: 'RESOLVE_EFFECT_STEP_V2', payload: { chosenTargetIds } });
            this.clientGameState.choosingEffectTargetMode = false;
            this.clientGameState.selectingFromTrashMode = false;
            this.clientGameState.selectedCard = null;
            this.clientGameState.selectableCards = [];
            this.clientGameState.availableActions = [];
            this.refreshScene();
        } else if (this.clientGameState.promptMode) {
            this.socket.emit('playerAction', { type: 'RESOLVE_EFFECT_STEP_V2', payload: { response: 'confirm' } });
            this.clientGameState.promptMode = false;
            this.clientGameState.selectedCard = null;
            this.clientGameState.selectableCards = [];
            this.clientGameState.availableActions = [];
            this.refreshScene();
        } else if (this.serverGameState.pendingAction?.type === 'EFFECT_STEP_RESOLUTION' && this.serverGameState.pendingAction?.expectedResponseType === 'revealCards') {
            this.socket.emit('playerAction', { type: 'RESOLVE_EFFECT_STEP_V2', payload: { } });
        }
    }

    #handleResetClicked() {
        if (this.clientGameState.searchDeckMode) {
            this.clientGameState.searchDeckReorderMode = false;
            this.clientGameState.searchDeckReorderSlots = [];
            this.clientGameState.selectedCard = null;
            this.refreshScene();
            return;
        } else if (this.clientGameState.choosingReturnDonsMode) {
            this.clientGameState.donsSelectedForReturn = [];
            this.clientGameState.overridePrompt = `Return ${this.serverGameState.pendingAction.requiredSelectionCount} DON!!s - (0/${this.serverGameState.pendingAction.requiredSelectionCount})`;
            this.refreshScene();
        }
    }

    #handleKeepHandClicked() {
        this.socket.emit('playerAction', { type: 'RESOLVE_MULLIGAN_PROMPT', payload: { keep: true } });
        this.clientGameState.decisionMade = true;
    }

    #handleReplaceHandClicked() {
        this.socket.emit('playerAction', { type: 'RESOLVE_MULLIGAN_PROMPT', payload: { keep: false } });
        this.clientGameState.decisionMade = true;
    }

    #generateAnimations(newGameState) {
        const animations = [];

        if (this.serverGameState.isMyTurn !== newGameState.isMyTurn) {
            animations.push({ type: 'turn_changed' });
        }

        // check if it's my turn
        if (this.serverGameState.currentPhase === 'End' && newGameState.currentPhase === 'Refresh' && newGameState.isMyTurn) {
            animations.push({ type: 'your_turn' });
            animations.push({ type: 'my_refresh_phase', prevGameState: this.serverGameState, newGameState });
        }

        // check if its opponent's turn
        if (this.serverGameState.currentPhase === 'End' && newGameState.currentPhase === 'Refresh' && !newGameState.isMyTurn) {
            animations.push({ type: 'opponents_turn' });
        }

        let cardsDrawn = [];
        newGameState.player.hand.forEach(card => {
            const wasCardInHand = this.serverGameState.player?.hand.find(prevCard => prevCard.instanceId === card.instanceId);
            if (!wasCardInHand && card.previousLocation === 'Deck') {
                cardsDrawn.push(card);
            }
        });

        if (cardsDrawn.length) {
            animations.push({ type: 'player_drew_cards', cardsDrawn });
        }

        // check if dons were rested
        let donsRestedCount = 0;
        newGameState.player.active_don.forEach(don => {
            this.serverGameState.player.active_don.forEach(prevDon => {
                if (prevDon.instanceId === don.instanceId) {
                    if (!prevDon.isRested && don.isRested) {
                        donsRestedCount++;
                    }
                }
            });
        });

        if (donsRestedCount > 0) {
            animations.push({ type: 'player_rested_dons', donsRestedCount });
        }

        let opponentDonsRestedCount = 0;
        newGameState.opponent.active_don.forEach(don => {
            this.serverGameState.opponent.active_don.forEach(prevDon => {
                if (prevDon.instanceId === don.instanceId) {
                    if (!prevDon.isRested && don.isRested) {
                        opponentDonsRestedCount++;
                    }
                }
            });
        });
        
        if (opponentDonsRestedCount > 0) {
            animations.push({ type: 'opponent_rested_dons', opponentDonsRestedCount });
        }

        // check if the player played a character card
        newGameState.player.field_characters.forEach((character, index) => {
            if (this.serverGameState.pendingAction?.type !== 'TRIGGER_STEP' && character.previousLocation === 'Hand' && character.location === 'field_characters' && this.serverGameState.player.hand.find(card => card.instanceId === character.instanceId)) {
                animations.push({ type: 'player_played_character', instanceId: character.instanceId, numOfCharacters: newGameState.player.field_characters.length, fieldIndex: index });
            }
        });

        // check if player played a stage card
        if (this.serverGameState.pendingAction?.type !== 'TRIGGER_STEP' && newGameState.player.field_stages[0]?.previousLocation === 'Hand' && newGameState.player.field_stages[0]?.location === 'field_stages' && this.serverGameState.player.hand?.find(card => card.instanceId === newGameState.player.field_stages[0]?.instanceId)) {
            animations.push({ type: 'player_played_stage', instanceId: newGameState.player.field_stages[newGameState.player.field_stages.length-1].instanceId });
        }

        // check if the opponent played a character card
        newGameState.opponent.field_characters.forEach((character, index) => {
            if (this.serverGameState.pendingAction?.type !== 'TRIGGER_STEP' && character.previousLocation === 'Hand' && character.location === 'field_characters' && this.serverGameState.opponent.hand?.find(card => card.instanceId === character.instanceId)) {
                animations.push({ type: 'opponent_played_character', cardInstance: character, numOfCharacters: newGameState.opponent.field_characters.length, fieldIndex: index });
            }
        });

        // check if opponent character was returned to hand
        newGameState.opponent?.hand?.forEach(card => {
            if (this.serverGameState.opponent?.field_characters.find(character => character.instanceId === card.instanceId)) {
                animations.push({ type: 'opponent_returned_character_to_hand', cardInstanceId: card.instanceId, newHand: newGameState.opponent.hand });
            }
        });

        if (newGameState.pendingAction?.type === 'EFFECT_STEP_EXECUTION' && ['field_characters', 'field_stages', 'Leader'].includes(newGameState.pendingAction?.cardInstance?.location)) {
            animations.push({ type: 'effect_step_started', cardInstance: newGameState.pendingAction.cardInstance });
        }

        // check if player attached don to leader
        newGameState.player.leader.attachedDon.forEach(don => {
            const currentDonFromPrevState = this.serverGameState.player.active_don.find(d => d.instanceId === don.instanceId);
            if (currentDonFromPrevState) {
                const wasRested = currentDonFromPrevState.isRested;
                animations.push({ type: 'player_attached_don', targetCardInstanceId: newGameState.player.leader.instanceId, wasRested });
            }
        });

        // check if player attached don to character
        newGameState.player.field_characters.forEach((character) => {
            character.attachedDon.forEach(don => {
                const currentDonFromPrevState = this.serverGameState.player.active_don.find(d => d.instanceId === don.instanceId);
                if (currentDonFromPrevState) {
                    const wasRested = currentDonFromPrevState.isRested;
                    animations.push({ type: 'player_attached_don', targetCardInstanceId: character.instanceId, wasRested });
                }
            });
        });

        // check if opponent attached don to leader
        newGameState.opponent.leader.attachedDon.forEach(don => {
            const currentDonFromPrevState = this.serverGameState.opponent.active_don.find(d => d.instanceId === don.instanceId);
            if (currentDonFromPrevState) {
                const wasRested = currentDonFromPrevState.isRested;
                animations.push({ type: 'opponent_attached_don', targetCardInstanceId: newGameState.opponent.leader.instanceId, wasRested });
            }
        });

        // check if opponent attached don to character
        newGameState.opponent.field_characters.forEach((character) => {
            character.attachedDon.forEach(don => {
                const currentDonFromPrevState = this.serverGameState.opponent.active_don.find(d => d.instanceId === don.instanceId);
                if (currentDonFromPrevState) {
                    const wasRested = currentDonFromPrevState.isRested;
                    animations.push({ type: 'opponent_attached_don', targetCardInstanceId: character.instanceId, wasRested });
                }
            });
        }); 

        // check if blocker step started
        if (!this.serverGameState.pendingAction && newGameState.pendingAction?.type === 'BLOCKER_STEP') {
            animations.push({ type: 'blocker_step_started' });
        }

        // check if counter step has started
        if ((!this.serverGameState.pendingAction || this.serverGameState.pendingAction?.type === 'BLOCKER_STEP') && newGameState.pendingAction?.type === 'COUNTER_STEP') {
            animations.push({ type: 'counter_step_started' });
        }

        const leavingCounterStep = this.serverGameState.pendingAction?.type === 'COUNTER_STEP' && (newGameState.pendingAction?.type == 'TRIGGER_STEP' || !newGameState.pendingAction);
        if (leavingCounterStep) {
            const { attackerId, defenderId } = this.serverGameState.pendingAction;
            const attackerOldState = findCardInstance(this.serverGameState, attackerId);
            const defenderOldState = findCardInstance(this.serverGameState, defenderId);

            const defenderNewState = findCardInstance(newGameState, defenderId);
            const successfulAttack = (defenderNewState.type === 'Character' && defenderNewState.location === 'trash') || (defenderOldState.type === 'Leader' && attackerOldState.currentPower >= defenderOldState.currentPower);
            animations.push({ type: 'attack_happened', attackerId, defenderId, successfulAttack });
        }

        if (this.serverGameState.pendingAction?.type !== 'TRIGGER_STEP' && newGameState.pendingAction?.type === 'TRIGGER_STEP' && !newGameState.isMyTurn) {
            animations.push({ type: 'player_taking_life_card', lifeCard: newGameState.player.hand[newGameState.player.hand.length-1] });
        }

        // check if player used a counter character
        if (this.serverGameState.pendingAction?.type === 'COUNTER_STEP' && newGameState.pendingAction?.type === 'COUNTER_STEP' && newGameState.player.trash[newGameState.player.trash.length-1]?.previousLocation === 'Hand' && this.serverGameState.player.hand?.find(card => card.instanceId === newGameState.player.trash[newGameState.player.trash.length-1]?.instanceId)) {
            animations.push({ type: 'use_counter_character', cardInstance: newGameState.player.trash[newGameState.player.trash.length-1] });
        }

        const cardsWithChangedPower = [];

        if (this.serverGameState.player && newGameState.player.leader.currentPower !== this.serverGameState.player.leader.currentPower) {
            cardsWithChangedPower.push({ instanceId: newGameState.player.leader.instanceId, prevPower: this.serverGameState.player.leader.currentPower, currentPower: newGameState.player.leader.currentPower });
        }

        newGameState.player.field_characters.forEach(character => {
            this.serverGameState.player?.field_characters.forEach(prevCharacter => {
                if (prevCharacter.instanceId === character.instanceId) {
                    if (prevCharacter.currentPower !== character.currentPower) {
                        cardsWithChangedPower.push({ instanceId: character.instanceId, prevPower: prevCharacter.currentPower, currentPower: character.currentPower });
                    }
                }
            });
        });
        
        if (this.serverGameState.opponent && newGameState.opponent.leader.currentPower !== this.serverGameState.opponent.leader.currentPower) {
            cardsWithChangedPower.push({ instanceId: newGameState.opponent.leader.instanceId, prevPower: this.serverGameState.opponent.leader.currentPower, currentPower: newGameState.opponent.leader.currentPower });
        }

        newGameState.opponent.field_characters.forEach(character => {
            this.serverGameState.opponent?.field_characters.forEach(prevCharacter => {
                if (prevCharacter.instanceId === character.instanceId) {
                    if (prevCharacter.currentPower !== character.currentPower) {
                        cardsWithChangedPower.push({ instanceId: character.instanceId, prevPower: prevCharacter.currentPower, currentPower: character.currentPower });
                    }
                }
            });
        });
        
        if (cardsWithChangedPower.length) {
            animations.push({ type: 'power_changed', cardsWithChangedPower });
        }

        if (newGameState.pendingAction?.type === 'REVEAL_CARDS' && newGameState.pendingAction.playerId === this.serverGameState.opponent.playerId) {
            animations.push({ type: 'opponent_reveal_cards', cards: newGameState.pendingAction.cards });
        }

        if (newGameState.pendingAction?.type === 'PLAY_ANIMATION') {
            animations.push({ type: 'play_animation', gameState: newGameState });
        }

        return animations;
    }

    #handleTrashClicked(isPlayer) {
        if (isPlayer) {
            this.sceneManager.togglePlayerTrashOverlay();
            this.sceneManager.hideOpponentTrashOverlay();
        } else {
            this.sceneManager.toggleOpponentTrashOverlay();
            this.sceneManager.hidePlayerTrashOverlay();
        }
    }

    #handleKeepTriggerClicked() {
        this.socket.emit('playerAction', { type: 'RESOLVE_TRIGGER_STEP', payload: { activate: false } });
    }

    #handleActivateTriggerClicked() {
        this.socket.emit('playerAction', { type: 'RESOLVE_TRIGGER_STEP', payload: { activate: true } });
    }

    startBackgroundMusic() {
        AssetsLoader.getSound(AssetsLoader.eDefaultAssetsAliases.BG_MUSIC)?.play();
    }

    stopBackgroundMusic() {
        AssetsLoader.getSound(AssetsLoader.eDefaultAssetsAliases.BG_MUSIC)?.stop();
    }

    #handleCloseCardInfo() {
        this.sceneManager.hideCardInfo();
    }

    #handleSettingsButtonClicked() {
        this.sceneManager.toggleSettingsOverlay();
    }

    #handleSurrenderButtonClicked() {
        this.socket.emit('playerAction', { type: 'SURRENDER' });
    }

    #handleCloseFtuePopupClicked() {
        this.sceneManager.hideFtuePopup();

        if (this.storage) {
            const ftuePopupShown = this.storage.getItem('ftuePopupShown');
            this.storage.setItem('ftuePopupShown', +ftuePopupShown + 1);
        }
    }

    #handleRematchButtonClicked() {
        // this.socket.emit('playerAction', { type: 'REMATCH' });
        this.onRematchClicked?.();
    }

    #handleQuitMatchButtonClicked() {
        // this.socket.emit('playerAction', { type: 'QUIT_MATCH' });
        this.onQuitMatchClicked?.();
    }

    #handleSearchDeckReorderSlotsUpdated(newReorderSlots) {
        this.clientGameState.searchDeckReorderSlots = newReorderSlots;
    }

    #handleNoSelectionClicked() {
        if (this.clientGameState.searchDeckMode) {
            if (this.serverGameState.pendingAction.currentStepData?.details?.reorder) {
                this.clientGameState.searchDeckReorderMode = true;
                this.clientGameState.searchDeckReorderSlots = [...this.serverGameState.pendingAction.searchCards];
                this.clientGameState.selectedCard = null;
                this.clientGameState.searchDeckChooseReorderPosition = false;
                if (this.serverGameState.pendingAction.currentStepData?.details?.placeRest === 'Choose') {
                    this.clientGameState.searchDeckChooseReorderPosition = true;
                }
                this.refreshScene();
            } else {
                this.socket.emit('playerAction', { type: 'RESOLVE_EFFECT_STEP_V2', payload: { chosenTargetIds: [] } });
                this.clientGameState.searchDeckMode = false;
                this.clientGameState.selectedCard = null;
                this.refreshScene();
            }
        } else if (this.clientGameState.selectingFromTrashMode) {
            this.socket.emit('playerAction', { type: 'RESOLVE_EFFECT_STEP_V2', payload: { chosenTargetIds: [] } });
            this.clientGameState.choosingEffectTargetMode = false;
            this.clientGameState.selectingFromTrashMode = false;
            this.clientGameState.selectedCard = null;
            this.clientGameState.selectableCards = [];
            this.clientGameState.availableActions = [];
            this.refreshScene();
        } else if (this.clientGameState.choosingFromOpponentHandMode) {
            this.socket.emit('playerAction', { type: 'RESOLVE_EFFECT_STEP_V2', payload: { chosenTargetIds: [] } });
            this.clientGameState.choosingFromOpponentHandMode = false;
            this.clientGameState.selectedCard = null;
            this.clientGameState.selectableCards = [];
            this.clientGameState.playableCards = [];
            this.refreshScene();
        }
    }

    #handlePlaceOnTopClicked() {
        const order = this.clientGameState.searchDeckReorderSlots.map(cardInstance => cardInstance.instanceId);
        const chosenTargetIds = this.clientGameState.selectedCard ? [this.clientGameState.selectedCard] : [];
        this.socket.emit('playerAction', { type: 'RESOLVE_EFFECT_STEP_V2', payload: { chosenTargetIds, order, chosenPosition: 'DeckTop' } });
        this.clientGameState.searchDeckMode = false;
        this.clientGameState.searchDeckReorderMode = false;
        this.clientGameState.selectedCard = null;
        this.clientGameState.searchDeckReorderSlots = [];
        this.refreshScene();
    }

    #handlePlaceOnBottomClicked() {
        const order = this.clientGameState.searchDeckReorderSlots.map(cardInstance => cardInstance.instanceId);
        const chosenTargetIds = this.clientGameState.selectedCard ? [this.clientGameState.selectedCard] : [];
        this.socket.emit('playerAction', { type: 'RESOLVE_EFFECT_STEP_V2', payload: { chosenTargetIds, order, chosenPosition: 'DeckBottom' } });
        this.clientGameState.searchDeckMode = false;
        this.clientGameState.searchDeckReorderMode = false;
        this.clientGameState.selectedCard = null;
        this.clientGameState.searchDeckReorderSlots = [];
        this.refreshScene();
    }
}
